import type { ProjectDefinition } from "../../../types";
import { type FuncionProjectSpec, conditions, seededRandom } from "../spec";
import { average, median, percentileInc, rankEq } from "./estadistica";

const SHEET = "MT Proveedores";
const TABLE = "Proveedores";
const CRITERIOS = ["Precio", "Calidad", "Entrega", "Servicio"] as const;
// Weights in whole percent so every score is exact to the hundredth.
const PESOS = [35, 30, 20, 15];

const NOMBRES = [
  "Aceros del Norte", "Plásticos Unidos", "Empaques Rivera", "Química Industrial MX", "Tornillería Central",
  "Logística Express", "Papelera del Bajío", "Electro Partes", "Maderas Finas", "Textiles Aztecas",
  "Pinturas Prisma", "Herrajes Sol", "Vidrios Templados", "Cartón y Más", "Componentes Omega",
  "Filtros Industriales",
] as const;

const rnd = seededRandom(1616);
type Row = [string, number, number, number, number];
const puntaje = (r: Row) => (r[1] * PESOS[0] + r[2] * PESOS[1] + r[3] * PESOS[2] + r[4] * PESOS[3]) / 100;

// Proveedor, Precio, Calidad, Entrega, Servicio (1–10). Scores are kept
// distinct: Excel adds 0.35 + 0.30… in floating point, so two "equal"
// scores could differ in the last digit and break ties differently.
const usados = new Set<number>();
const ROWS: Row[] = NOMBRES.map((n) => {
  for (;;) {
    const row: Row = [n, rnd.int(4, 10), rnd.int(4, 10), rnd.int(4, 10), rnd.int(4, 10)];
    if (!usados.has(puntaje(row))) {
      usados.add(puntaje(row));
      return row;
    }
  }
});
const puntajes = ROWS.map(puntaje);
const p75 = percentileInc(puntajes, 0.75);
const med = median(puntajes);
const promedio = average(puntajes);
const top3 = [...puntajes].sort((a, b) => b - a).slice(0, 3);
const mejor = ROWS[puntajes.indexOf(Math.max(...puntajes))][0];

export const spec: FuncionProjectSpec = {
  projectId: "funciones-proveedores",
  sheets: [
    {
      name: SHEET,
      title: "Evaluación de proveedores",
      subtitle: "Calificaciones del 1 al 10 en cuatro criterios — compras define cuánto pesa cada uno",
      tables: [
        {
          name: TABLE,
          anchor: "A4",
          headers: ["Proveedor", ...CRITERIOS],
          rows: ROWS,
          answerColumns: ["Puntaje", "Lugar", "Clase", "Vs promedio"],
          formats: { Puntaje: "0.00", "Vs promedio": "+0.00;-0.00;0.00" },
        },
        {
          name: "Pesos",
          anchor: "L4",
          headers: [...CRITERIOS],
          rows: [PESOS.map((p) => p / 100)],
          formats: { Precio: "0%", Calidad: "0%", Entrega: "0%", Servicio: "0%" },
        },
      ],
      answers: [
        { address: "M8", label: "Tarea 5 · Mejor proveedor" },
        { address: "M9", label: "Tarea 6 · Promedio del top 3", format: "0.00" },
      ],
    },
  ],
  checks: {
    1: { kind: "column", sheet: SHEET, table: TABLE, column: "Puntaje", fns: ["SUMPRODUCT"], expected: puntajes },
    2: { kind: "column", sheet: SHEET, table: TABLE, column: "Lugar", fns: ["RANK.EQ"], expected: puntajes.map((p) => rankEq(p, puntajes)) },
    3: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Clase", fns: ["IFS", "PERCENTILE.INC", "MEDIAN"], exact: true,
      expected: puntajes.map((p) => (p >= p75 ? "A" : p >= med ? "B" : "C")),
    },
    4: { kind: "column", sheet: SHEET, table: TABLE, column: "Vs promedio", fns: ["AVERAGE"], expected: puntajes.map((p) => p - promedio) },
    5: { kind: "cell", sheet: SHEET, address: "M8", fns: ["INDEX|XLOOKUP", "MAX"], expected: mejor },
    6: { kind: "cell", sheet: SHEET, address: "M9", fns: ["AVERAGE", "LARGE", "SEQUENCE"], expected: average(top3) },
  },
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-proveedores",
  title: "Reto final: Evaluación de proveedores",
  subtitle: "Puntaje ponderado, ranking y clasificación ABC",
  description:
    "Compras evalúa a 16 proveedores en precio, calidad, entrega y servicio, y cada criterio pesa distinto (tabla Pesos). Con eso hay que calcular el puntaje de cada proveedor, su lugar, una clase A/B/C, qué tan lejos está del promedio, quién es el mejor y el promedio de los tres primeros.\n\nAquí se juntan SUMAPRODUCTO, JERARQUIA.EQV, PERCENTIL.INC, MEDIANA, PROMEDIO y K.ESIMO.MAYOR.",
  topics: [
    "SUMAPRODUCTO por fila contra una fila de pesos",
    "Ranking con empates",
    "Clasificación ABC con percentil y mediana",
    "Diferencia contra el promedio",
    "Promedio de los N mejores con SECUENCIA",
  ],
  tasks: [
    {
      id: 1,
      level: "intermedio",
      title: "Tarea 1: Puntaje ponderado",
      description:
        "En la hoja <strong>MT Proveedores</strong>, columna <strong>Puntaje</strong>: multiplica cada calificación por su peso de la tabla <strong>Pesos</strong> (L4) y suma: Precio × 35% + Calidad × 30% + Entrega × 20% + Servicio × 15%.",
      solution: [
        `Selecciona ${F("F5")}.`,
        `${F("Proveedores[@[Precio]:[Servicio]]")} son las 4 calificaciones de la fila y ${F("Pesos[[Precio]:[Servicio]]")} los 4 pesos: SUMAPRODUCTO los multiplica uno a uno y suma.`,
        `Escribe ${F("=SUMAPRODUCTO(Proveedores[@[Precio]:[Servicio]],Pesos[[Precio]:[Servicio]])")}.`,
      ],
      expectedResult: "Puntaje de 4 a 10",
      finalFormula: "=SUMAPRODUCTO(Proveedores[@[Precio]:[Servicio]],Pesos[[Precio]:[Servicio]])",
    },
    {
      id: 2,
      level: "intermedio",
      title: "Tarea 2: Lugar",
      description: "En la columna <strong>Lugar</strong>: el lugar de cada proveedor por Puntaje (1 = el mejor). Si dos empatan, comparten lugar.",
      solution: [`Selecciona ${F("G5")}.`, `Escribe ${F("=JERARQUIA.EQV([@Puntaje],[Puntaje])")}.`],
      expectedResult: "1 a 16",
      finalFormula: "=JERARQUIA.EQV([@Puntaje],[Puntaje])",
    },
    {
      id: 3,
      level: "reto",
      title: "Tarea 3: Clasificación ABC",
      description: conditions("En la columna <strong>Clase</strong>:", [
        "Puntaje mayor o igual al <strong>percentil 75</strong> de los puntajes → <strong>A</strong>",
        "Mayor o igual a la <strong>mediana</strong> → <strong>B</strong>",
        "El resto → <strong>C</strong>",
      ]),
      solution: [
        `Selecciona ${F("H5")}.`,
        `Escribe ${F('=SI.CONJUNTO([@Puntaje]>=PERCENTIL.INC([Puntaje],75%),"A",[@Puntaje]>=MEDIANA([Puntaje]),"B",VERDADERO,"C")')}.`,
      ],
      expectedResult: "A / B / C",
      finalFormula: '=SI.CONJUNTO([@Puntaje]>=PERCENTIL.INC([Puntaje],75%),"A",[@Puntaje]>=MEDIANA([Puntaje]),"B",VERDADERO,"C")',
    },
    {
      id: 4,
      level: "reto",
      title: "Tarea 4: Diferencia contra el promedio",
      description: "En la columna <strong>Vs promedio</strong>: cuántos puntos está cada proveedor <strong>arriba o abajo</strong> del puntaje promedio de todos.",
      solution: [`Selecciona ${F("I5")}.`, `Escribe ${F("=[@Puntaje]-PROMEDIO([Puntaje])")}.`],
      expectedResult: "+0.85 o -1.20",
      finalFormula: "=[@Puntaje]-PROMEDIO([Puntaje])",
    },
    {
      id: 5,
      level: "reto",
      title: "Tarea 5: Mejor proveedor",
      description: "En <strong>M8</strong>: el nombre del proveedor con el <strong>Puntaje más alto</strong>.",
      solution: [
        `Selecciona ${F("M8")}.`,
        `Escribe ${F("=INDICE(Proveedores[Proveedor],COINCIDIR(MAX(Proveedores[Puntaje]),Proveedores[Puntaje],0))")}.`,
      ],
      expectedResult: "Nombre del mejor proveedor",
      finalFormula: "=INDICE(Proveedores[Proveedor],COINCIDIR(MAX(Proveedores[Puntaje]),Proveedores[Puntaje],0))",
    },
    {
      id: 6,
      level: "reto",
      title: "Tarea 6: Promedio de los 3 mejores",
      description: conditions("En <strong>M9</strong>: el promedio de los <strong>3 puntajes más altos</strong>, en una sola fórmula:", [
        "<strong>SECUENCIA(3)</strong> genera 1, 2, 3",
        "<strong>K.ESIMO.MAYOR</strong> con esa secuencia devuelve los 3 mejores puntajes a la vez",
        "<strong>PROMEDIO</strong> los resume",
      ]),
      solution: [
        `Selecciona ${F("M9")}.`,
        `Escribe ${F("=PROMEDIO(K.ESIMO.MAYOR(Proveedores[Puntaje],SECUENCIA(3)))")}.`,
        "Cambia el 3 por 5 para tener el promedio del top 5.",
      ],
      expectedResult: "Promedio del top 3",
      finalFormula: "=PROMEDIO(K.ESIMO.MAYOR(Proveedores[Puntaje],SECUENCIA(3)))",
    },
  ],
};
